import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Info, Loader2 } from "lucide-react";
import type { DocumentEditorContainerComponent } from "@syncfusion/ej2-react-documenteditor";
import { useAccessToken } from "@/auth/useAccessToken";
import { driveApi, explainSaveError, isDriveUrl, type DriveItem } from "@/api/driveApi";
import { useSaveTracker } from "@/lib/useSaveTracker";
import { CloseFileButton, FileTitle } from "@/features/shell/FileTitle";
import { ShellHeaderPortal, useShellLeaveGuard } from "@/features/shell/ShellHeader";
import type { EditorLaunchState } from "@/features/home/homeModel";
import { DriveOpenDialog } from "./DriveOpenDialog";
import { buildDocTemplate } from "./docTemplates";
import { driveFileApi } from "@/features/drive-file/driveFileApi";
import { DriveLocation } from "@/features/drive-file/DriveLocation";
import { ShareButton } from "@/features/drive-file/ShareDialog";
import {
    HistoryButton, VersionHistoryPanel, VIEWER_HISTORY_MESSAGE, isHistoryShortcut
} from "@/features/drive-file/VersionHistoryPanel";
import {
    CommentsButton, CommentsPanel, isCommentsShortcut, useOpenCommentCount
} from "@/features/drive-file/CommentsPanel";
import { docsCommentsAdapter } from "./docsComments";
import { DocModeMenu, type DocMode } from "./DocModeMenu";
import { DocSaveStatusItem, DocStatusItems, NativeStatusBarSlot, OutlinePane, ReviewPane } from "./DocPanes";
import { navigateToHeading, readFlag, useDocInsights, writeFlag, type DocRevision, type OutlineItem } from "./docInsights";

const OUTLINE_FLAG = "athavita.docs.outlineOpen";

// The page edge on the canvas. The page shadow (docsEditor.css) does most of
// the work; Syncfusion's default outline is solid black. A view setting, not
// document content — setting it never marks the document unsaved.
const PAGE_OUTLINE = "rgba(0, 0, 0, 0.08)";

// The DocumentEditor bundle is ~5 MB even minified — lazy-load so the
// rest of the SPA mounts fast on cold sign-in.
const DocumentEditorHost = lazy(() =>
    // memo: the page re-renders on every caret move (status bar, outline
    // highlight); the editor host's props are stable, so it doesn't.
    import("./DocumentEditorHost").then((m) => ({ default: memo(m.DocumentEditorHost) }))
);

// The DocumentEditor container is configured with a `serviceUrl` in
// DocumentEditorHost that handles .docx / .doc / .rtf → SFDT conversion,
// so opening native Word documents works as long as the service is
// reachable. SFDT + TXT stay on the pure-client path (no round-trip).
const SFDT_EXTENSION_RE = /\.sfdt$/i;
const TEXT_EXTENSION_RE = /\.txt$/i;
const SERVER_CONVERTED_EXT_RE = /\.(docx?|rtf)$/i;
const NATIVE_SUPPORTED_EXT_RE = /\.(sfdt|txt|docx?|rtf)$/i;

// Edits the editor reports shortly after a load (layout passes, the
// mammoth path's insertHtml) belong to the load, not to the user.
const LOAD_SETTLE_MS = 250;

type DocEditor = NonNullable<DocumentEditorContainerComponent["documentEditor"]>;
type DocFormat = "docx" | "sfdt" | "txt";
type SaveMode = "manual" | "auto";

const BLOB_TYPE: Record<DocFormat, "Docx" | "Sfdt" | "Txt"> = { docx: "Docx", sfdt: "Sfdt", txt: "Txt" };

/** The Drive file the editor is bound to — Save writes a new version of it. */
interface TrackedFile {
    id: string;
    name: string;
    parentId: string | null;
    /** "viewer" / "editor" when someone else shared the file; unset for your own. */
    role?: string | null;
}

// Dropdown-style File menu items — matches Sheet's hamburger menu
// (no backstage). Close returns to Docs home. Clicks are dispatched
// via the container's `fileMenuItemClick` event, handled in
// `onFileMenuItemClick` below.
// Icon classes match Sheet's File menu exactly so both dropdowns
// render the same glyphs: generic `e-icons` family instead of the
// document-editor-specific `e-de-ctnr-*` variants.
const FILE_MENU_ITEMS = [
    { id: "athavita-new", text: "New", iconCss: "e-icons e-add-icon" },
    { id: "athavita-open", text: "Open", iconCss: "e-icons e-folder-open" },
    { id: "athavita-open-drive", text: "Open from Drive", iconCss: "e-icons e-folder-open" },
    { id: "athavita-save", text: "Save", iconCss: "e-icons e-save" },
    { id: "athavita-save-as", text: "Save As…", iconCss: "e-icons e-save" },
    { id: "athavita-history", text: "Version history", iconCss: "e-icons e-history" },
    { id: "athavita-print", text: "Print", iconCss: "e-icons e-print" },
    { id: "athavita-close", text: "Close", iconCss: "e-icons e-close" }
];

export function DocsPage() {
    const [params] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const token = useAccessToken();
    // Autosave fires from a timer — read the freshest token / navigate.
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    // Launch parameters are read once: the URL is rewritten as files are
    // opened and saved, and that must never re-trigger a load.
    const launchRef = useRef({
        fileId: params.get("fileId"),
        openFile: params.get("openFile"),
        fileName: params.get("fileName"),
        localFile: (location.state as EditorLaunchState | null)?.localFile ?? null,
        template: (location.state as EditorLaunchState | null)?.template ?? null
    });

    const editorRef = useRef<DocumentEditorContainerComponent>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const trackedRef = useRef<TrackedFile | null>(null);
    const [tracked, setTrackedState] = useState<TrackedFile | null>(null);
    const [fileName, setFileName] = useState(launchRef.current.fileName ?? "Untitled.docx");
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);
    const settleTimerRef = useRef<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const saveNowRef = useRef<(mode: SaveMode) => Promise<void>>(async () => {});
    const { status, markDirty, markClean, setSuppressed, runSave, hasUnsavedChanges, whenSaved } = useSaveTracker({
        onAutosave: () => void saveNowRef.current("auto"),
        onError: setError
    });

    // Binds the editor to a Drive file (null = unbound) and mirrors the
    // binding into the URL — `?fileId=` only — so a reload reopens it.
    const setTracked = useCallback((file: TrackedFile | null) => {
        const previousId = trackedRef.current?.id ?? null;
        trackedRef.current = file;
        setTrackedState(file);
        if (file) setFileName(file.name);
        if ((file?.id ?? null) !== previousId) {
            navigateRef.current(
                { search: file ? `?fileId=${encodeURIComponent(file.id)}` : "" },
                { replace: true }
            );
        }
    }, []);

    const confirmDiscard = useCallback(
        () => !hasUnsavedChanges() || window.confirm("You have unsaved changes that will be lost. Continue?"),
        [hasUnsavedChanges]
    );
    useShellLeaveGuard(confirmDiscard);

    // Close (top bar X / File → Close) → Docs home. A save in flight finishes
    // first — never cut off; whatever is still unsaved after it asks first.
    // Replace: Back from home doesn't reopen the file just closed.
    const [closing, setClosing] = useState(false);
    const closingRef = useRef(false);
    const closeFile = useCallback(async () => {
        if (closingRef.current) return;
        closingRef.current = true;
        setClosing(true);
        try {
            await whenSaved();
            if (hasUnsavedChanges() && !window.confirm("You have unsaved changes. Close without saving?")) return;
            markClean();
            navigateRef.current("/", { replace: true });
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    }, [hasUnsavedChanges, markClean, whenSaved]);

    // Wraps every document load. Change events the load itself causes are
    // ignored; a failed load leaves the previous document's state alone.
    const runLoad = useCallback(async (load: (editor: DocEditor) => Promise<void>): Promise<boolean> => {
        const editor = editorRef.current?.documentEditor;
        if (!editor) return false;
        if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        setSuppressed(true);
        try {
            await load(editor);
            markClean();
            return true;
        } catch (err) {
            setError(errorMessage(err));
            return false;
        } finally {
            loadingRef.current = false;
            setLoading(false);
            settleTimerRef.current = window.setTimeout(() => {
                settleTimerRef.current = null;
                setSuppressed(false);
            }, LOAD_SETTLE_MS);
        }
    }, [markClean, setSuppressed]);

    const openDriveFile = useCallback((file: TrackedFile) => runLoad(async (editor) => {
        if (!NATIVE_SUPPORTED_EXT_RE.test(file.name)) throw new Error(unsupportedMessage(file.name));
        const blob = await driveApi.downloadBlob(file.id, tokenRef.current);
        await openBlobInEditor(editor, blob, file.name, tokenRef.current);
        setTracked(file);
    }), [runLoad, setTracked]);

    const openLocalFile = useCallback((file: File) => runLoad(async (editor) => {
        if (!NATIVE_SUPPORTED_EXT_RE.test(file.name)) throw new Error(unsupportedMessage(file.name));
        await openBlobInEditor(editor, file, file.name, tokenRef.current);
        setTracked(null);
        setFileName(file.name);
    }), [runLoad, setTracked]);

    const loadInitial = useCallback(() => {
        const editor = editorRef.current?.documentEditor;
        if (!editor) return;
        // DocumentEditor's built-in Ctrl+S downloads an .sfdt copy. The
        // page-level handler below owns Ctrl+S (save to Drive).
        editor.keyDown = (args) => {
            const e = args.event;
            if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "s") args.isHandled = true;
            // Ctrl+Alt+Shift+H is the page's Version history shortcut.
            if (isHistoryShortcut(e)) args.isHandled = true;
            // Ctrl+Alt+M opens Comments.
            if (isCommentsShortcut(e)) args.isHandled = true;
        };

        const { fileId, openFile, fileName: launchName, localFile, template } = launchRef.current;
        if (template) {
            // A home-screen template: a new, unsaved document. Drop the
            // router state so a reload opens blank rather than re-applying it.
            navigateRef.current({ search: "" }, { replace: true });
            const built = buildDocTemplate(template);
            void runLoad(async (ed) => {
                if (built) ed.open(built.sfdt);
                else ed.openBlank();
                setTracked(null);
                setFileName(built?.fileName ?? "Untitled.docx");
            });
            return;
        }
        if (localFile) {
            // Drop the File from history state so a reload doesn't reopen it.
            navigateRef.current({ search: "" }, { replace: true });
            void openLocalFile(localFile);
            return;
        }
        if (fileId) {
            // Keep `fileId` (a reload reopens the file); drop Drive's
            // one-shot `openFile` / `fileName` hints.
            navigateRef.current({ search: `?fileId=${encodeURIComponent(fileId)}` }, { replace: true });
            void runLoad(async (ed) => {
                try {
                    const meta = await driveApi.getItem(fileId, tokenRef.current);
                    setFileName(meta.name);
                    if (!NATIVE_SUPPORTED_EXT_RE.test(meta.name)) throw new Error(unsupportedMessage(meta.name));
                    const blob = await driveApi.downloadBlob(meta.id, tokenRef.current);
                    await openBlobInEditor(ed, blob, meta.name, tokenRef.current);
                    setTracked(toTracked(meta));
                } catch (err) {
                    try { ed.openBlank(); } catch { /* already open */ }
                    throw err;
                }
            });
            return;
        }
        if (openFile) {
            navigateRef.current({ search: "" }, { replace: true });
            const name = launchName ?? "document.sfdt";
            void runLoad(async (ed) => {
                if (!NATIVE_SUPPORTED_EXT_RE.test(name)) throw new Error(unsupportedMessage(name));
                // Only Drive gets the bearer token — `openFile` comes from the URL.
                const headers = new Headers();
                if (tokenRef.current && isDriveUrl(openFile)) headers.set("Authorization", `Bearer ${tokenRef.current}`);
                const res = await fetch(openFile, { headers, credentials: "omit" });
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                await openBlobInEditor(ed, await res.blob(), name, tokenRef.current);
                setFileName(name);
            });
            return;
        }
        void runLoad(async (ed) => { ed.openBlank(); });
    }, [openLocalFile, runLoad, setTracked]);

    // Local-file picker for the "just let me edit a thing" path.
    const onLocalPick: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file || !confirmDiscard()) return;
        void openLocalFile(file);
    };

    // Save As → Drive, or a Save that can't version the open file: creates
    // a new file in the open file's folder, then binds the editor to it so
    // the next Save versions the copy rather than the original.
    const saveAsNewDriveFile = useCallback(async (fullName: string, format: DocFormat) => {
        const editor = editorRef.current?.documentEditor;
        if (!editor) return;
        await runSave(async () => {
            const blob = await editor.saveAsBlob(BLOB_TYPE[format]);
            assertNotEmpty(blob);
            const saved = await driveApi.uploadBlob(blob, fullName, trackedRef.current?.parentId ?? null, tokenRef.current);
            setTracked(toTracked(saved));
        });
    }, [runSave, setTracked]);

    const saveNow = useCallback(async (mode: SaveMode) => {
        const editor = editorRef.current?.documentEditor;
        if (!editor || loadingRef.current) return;
        const current = trackedRef.current;
        if (!current) {
            // Never saved — only an explicit Save asks where to put it.
            if (mode === "manual") setSaveAsOpen(true);
            return;
        }
        const format = versionFormat(current.name);
        if (!format) {
            // A .doc / .rtf can't take .docx bytes as a new version, so an
            // explicit Save writes a .docx copy next to it instead.
            if (mode === "manual") await saveAsNewDriveFile(`${stripExtension(current.name)}.docx`, "docx");
            return;
        }
        // A view-only share can't take a new version — don't autosave into a
        // guaranteed failure; an explicit Save explains why.
        if (mode === "auto" && current.role === "viewer") return;
        if (mode === "manual") setError(null);
        await runSave(async () => {
            const blob = await editor.saveAsBlob(BLOB_TYPE[format]);
            assertNotEmpty(blob);
            const saved = await driveApi
                .uploadVersion(current.id, blob, current.name, tokenRef.current)
                .catch((err: unknown) => { throw explainSaveError(err, current.role); });
            if (trackedRef.current?.id === saved.id) setTracked(toTracked(saved));
        });
    }, [runSave, saveAsNewDriveFile, setTracked]);
    saveNowRef.current = saveNow;

    // Version history — owners and editors of a Drive file only.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [commentsOpen, setCommentsOpen] = useState(false);
    const openHistory = useCallback(() => {
        const current = trackedRef.current;
        if (!current) setError("Save this document to Drive to keep a version history.");
        else if (current.role === "viewer") setError(VIEWER_HISTORY_MESSAGE);
        else {
            setCommentsOpen(false);
            setHistoryOpen(true);
        }
    }, []);
    useEffect(() => {
        if (!tracked || tracked.role === "viewer") setHistoryOpen(false);
        if (!tracked) setCommentsOpen(false);
    }, [tracked]);

    // Comments — anyone who can open the Drive file, viewers included.
    const commentCount = useOpenCommentCount(tracked?.id ?? null);
    const commentsAdapter = useMemo(() => docsCommentsAdapter(() => editorRef.current?.documentEditor), []);
    const openComments = useCallback(() => {
        if (!trackedRef.current) {
            setError("Save this document to Drive to comment on it.");
            return;
        }
        setHistoryOpen(false);
        // Comments and Suggestions share the right edge — one at a time
        // (Suggestions already closes Comments).
        setReviewOpen(false);
        setCommentsOpen(true);
    }, []);

    // Drive adds a new version holding the old bytes; reload it into the
    // editor and stay bound to the file.
    const restoreVersion = useCallback(async (versionId: string, discard: boolean) => {
        const current = trackedRef.current;
        if (!current) throw new Error("This document isn't in Drive.");
        if (!discard && hasUnsavedChanges()) throw new Error("Save or discard your changes before restoring.");
        // Dropping the edits first stops autosave writing them over the restore.
        if (discard) markClean();
        let restored: DriveItem;
        try {
            restored = await driveFileApi.restoreVersion(current.id, versionId, tokenRef.current);
        } catch (err) {
            if (discard) markDirty();
            throw err;
        }
        const ok = await openDriveFile({ ...toTracked(restored), role: restored.role ?? current.role ?? null });
        if (!ok) throw new Error("The version was restored in Drive, but the editor couldn't reload it — reopen the file to see it.");
    }, [hasUnsavedChanges, markClean, markDirty, openDriveFile]);

    // Ctrl+S → Save; Ctrl+Shift+S → Save As; Ctrl+Alt+Shift+H → Version history
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isHistoryShortcut(e)) {
                e.preventDefault();
                openHistory();
                return;
            }
            if (isCommentsShortcut(e)) {
                e.preventDefault();
                openComments();
                return;
            }
            if (e.key.toLowerCase() === "s" && (e.ctrlKey || e.metaKey) && !e.altKey) {
                e.preventDefault();
                if (e.shiftKey) setSaveAsOpen(true);
                else void saveNowRef.current("manual");
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [openHistory, openComments]);

    // Inline title rename. Bound to Drive → rename the file there; otherwise
    // it's just the name the next Save As suggests.
    const renameFile = useCallback(async (typed: string) => {
        const current = trackedRef.current;
        const next = keepExtension(typed, current?.name ?? fileName);
        if (!current) {
            setFileName(next);
            return;
        }
        const updated = await driveApi.renameItem(current.id, next, tokenRef.current);
        setTracked(toTracked(updated));
    }, [fileName, setTracked]);

    // Drive picker modal — triggered by our custom "Open from Drive"
    // toolbar item. Lists Word-compatible files from Drive (the home
    // screen's "Open file → From Drive" uses the same dialog).
    const [driveDialogOpen, setDriveDialogOpen] = useState(false);
    const openFromDrive = useCallback(() => setDriveDialogOpen(true), []);

    const pickDriveFile = useCallback((item: DriveItem) => {
        setDriveDialogOpen(false);
        if (!confirmDiscard()) return;
        void openDriveFile(toTracked(item));
    }, [confirmDiscard, openDriveFile]);

    // Native ribbon Open + Save icons trigger our unified dialogs.
    // - Open icon → choose Computer (local file picker) or Drive (picker modal)
    // - Save icon → Save (new Drive version); Save As icon → Save As dialog
    const [openChoiceOpen, setOpenChoiceOpen] = useState(false);
    const [saveAsOpen, setSaveAsOpen] = useState(false);

    const onToolbarClick = useCallback((args: { item?: { id?: string; text?: string }; cancel?: boolean }) => {
        const id = (args.item?.id ?? "").toLowerCase();
        const text = (args.item?.text ?? "").toLowerCase();
        if (id === "athavita-open-drive") { args.cancel = true; void openFromDrive(); return; }
        if (id === "open" || text === "open") { args.cancel = true; fileInputRef.current?.click(); return; }
        if (text === "save") { args.cancel = true; void saveNowRef.current("manual"); return; }
        if (id === "save-as" || text === "save as" || text === "save as…") {
            args.cancel = true;
            setSaveAsOpen(true);
        }
    }, [openFromDrive]);

    const onFileMenuItemClick = useCallback((args: { item?: { id?: string; text?: string }; cancel?: boolean }) => {
        const id = args.item?.id ?? "";
        if (id === "athavita-new") {
            if (!confirmDiscard()) return;
            void runLoad(async (editor) => {
                editor.openBlank();
                setTracked(null);
                setFileName("Untitled.docx");
            });
        } else if (id === "athavita-open") {
            // Directly open the OS file-picker, filtered by the hidden
            // <input type="file" accept=".docx,.doc,.rtf,.sfdt,.txt">.
            // Mirrors Sheet's native Open behaviour exactly.
            fileInputRef.current?.click();
        } else if (id === "athavita-open-drive") {
            void openFromDrive();
        } else if (id === "athavita-save") {
            void saveNowRef.current("manual");
        } else if (id === "athavita-save-as") {
            setSaveAsOpen(true);
        } else if (id === "athavita-history") {
            openHistory();
        } else if (id === "athavita-print") {
            try { editorRef.current?.documentEditor?.print(); } catch { /* noop */ }
        } else if (id === "athavita-close") {
            void closeFile();
        }
    }, [closeFile, confirmDiscard, openFromDrive, openHistory, runLoad, setTracked]);

    const handleSaveAsConfirm = useCallback(async (target: "drive" | "computer", name: string, type: DocFormat) => {
        setSaveAsOpen(false);
        const editor = editorRef.current?.documentEditor;
        if (!editor) return;
        setError(null);
        const fullName = `${stripExtension(name || "Untitled")}.${type}`;
        if (target === "drive") {
            await saveAsNewDriveFile(fullName, type);
            return;
        }
        try {
            downloadFile(await editor.saveAsBlob(BLOB_TYPE[type]), fullName);
        } catch (err) {
            setError(errorMessage(err));
        }
    }, [saveAsNewDriveFile]);

    // ── Mode (Editing / Suggesting / Viewing), Outline, Suggestions, counts ──
    const auth = useAuth();
    const currentUser = auth.user?.profile.name ?? auth.user?.profile.email ?? "";
    const [editorReady, setEditorReady] = useState(false);
    const [mode, setMode] = useState<DocMode>("editing");
    // View-only shares can't edit — always Viewing.
    const modeLocked = tracked?.role === "viewer";
    const effectiveMode: DocMode = modeLocked ? "viewing" : mode;
    const effectiveModeRef = useRef(effectiveMode);
    effectiveModeRef.current = effectiveMode;
    const [outlineOpen, setOutlineOpen] = useState(() => readFlag(OUTLINE_FLAG));
    const [reviewOpen, setReviewOpen] = useState(false);
    useEffect(() => writeFlag(OUTLINE_FLAG, outlineOpen), [outlineOpen]);

    const getEditor = useCallback(() => editorRef.current?.documentEditor, []);
    const { outline, stats, revisions, selectionWords, currentKey, refresh: refreshInsights, schedule: scheduleInsights, onSelection } =
        useDocInsights(getEditor);

    // Suggesting = the editor's track changes; Viewing = restrictEditing (read-only).
    const applyMode = useCallback((m: DocMode) => {
        const c = editorRef.current;
        const ed = c?.documentEditor;
        if (!c || !ed) return;
        const readOnly = m === "viewing";
        const track = m === "suggesting";
        try {
            if (c.restrictEditing !== readOnly) c.restrictEditing = readOnly;
            if (c.enableTrackChanges !== track) c.enableTrackChanges = track;
            c.dataBind();
            // A loaded document can carry its own track-changes setting — the mode wins.
            if (ed.enableTrackChanges !== track) ed.enableTrackChanges = track;
        } catch {
            /* editor not ready */
        }
    }, []);
    useEffect(() => {
        if (editorReady) applyMode(effectiveMode);
    }, [applyMode, editorReady, effectiveMode]);
    // Track-changes revisions carry the signed-in user's name.
    useEffect(() => {
        const c = editorRef.current;
        if (editorReady && c && currentUser && c.currentUser !== currentUser) c.currentUser = currentUser;
    }, [editorReady, currentUser]);

    const changeMode = useCallback((m: DocMode) => {
        setMode(m);
        if (m === "suggesting") setReviewOpen(true);
    }, []);
    // The ribbon's own Review → Track Changes toggle keeps the switch in step.
    const onTrackChange = useCallback((enabled: boolean) => {
        if (effectiveModeRef.current === "viewing") return;
        setMode(enabled ? "suggesting" : "editing");
    }, []);

    const onEditorCreated = useCallback(() => {
        const ed = editorRef.current?.documentEditor;
        if (ed) {
            try {
                ed.pageOutline = PAGE_OUTLINE;
            } catch {
                /* not ready — keep Syncfusion's outline */
            }
        }
        setEditorReady(true);
        loadInitial();
    }, [loadInitial]);
    const onEditorContentChange = useCallback(() => {
        markDirty();
        scheduleInsights();
    }, [markDirty, scheduleInsights]);
    const onEditorDocumentChange = useCallback(() => {
        applyMode(effectiveModeRef.current);
        scheduleInsights(50);
        // A comment's mark belonged to the previous document.
        commentsAdapter.reset();
    }, [applyMode, commentsAdapter, scheduleInsights]);
    // Caret moves feed the counts / outline and drop a comment's mark once the
    // caret leaves it. Stable, so the memoised editor host never re-renders.
    const onEditorSelection = useCallback(() => {
        onSelection();
        commentsAdapter.selectionMoved();
    }, [commentsAdapter, onSelection]);

    const goToHeading = useCallback((item: OutlineItem) => {
        const ed = editorRef.current?.documentEditor;
        if (ed && !navigateToHeading(ed, item)) refreshInsights();
        // Narrow screens: the outline overlays the page — get it out of the way.
        if (window.innerWidth < 768) setOutlineOpen(false);
    }, [refreshInsights]);

    const revisionAction = useCallback((action: (ed: DocEditor) => void) => {
        const ed = editorRef.current?.documentEditor;
        if (!ed) return;
        try {
            action(ed);
        } catch (err) {
            setError(errorMessage(err));
        }
        refreshInsights();
    }, [refreshInsights]);
    const canActOnRevisions = effectiveMode !== "viewing";
    const openReview = useCallback(() => {
        setCommentsOpen(false);
        setHistoryOpen(false);
        setReviewOpen(true);
        refreshInsights();
    }, [refreshInsights]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <ShellHeaderPortal>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <FileTitle
                        name={fileName}
                        status={status}
                        inDrive={tracked !== null}
                        loading={loading}
                        onRename={renameFile}
                        onRetry={() => void saveNowRef.current("manual")}
                        location={tracked ? (
                            <DriveLocation
                                fileId={tracked.id}
                                parentId={tracked.parentId}
                                shared={Boolean(tracked.role)}
                                tone="onPrimary"
                                className="hidden sm:flex"
                            />
                        ) : null}
                        accessory={
                            <>
                                {tracked ? (
                                    <HistoryButton
                                        tone="onPrimary"
                                        onClick={openHistory}
                                        disabledReason={tracked.role === "viewer" ? VIEWER_HISTORY_MESSAGE : null}
                                    />
                                ) : null}
                                <CommentsButton
                                    file={tracked}
                                    tone="onPrimary"
                                    openCount={commentCount.count}
                                    active={commentsOpen}
                                    onClick={commentsOpen ? () => setCommentsOpen(false) : openComments}
                                />
                            </>
                        }
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-2 pl-1">
                        <DocModeMenu mode={effectiveMode} onChange={changeMode} locked={modeLocked} />
                        <ShareButton file={tracked} tone="onPrimary" />
                        <CloseFileButton appName="Docs" busy={closing} onClose={() => void closeFile()} />
                    </div>
                </div>
            </ShellHeaderPortal>
            {historyOpen && tracked ? (
                <VersionHistoryPanel
                    file={tracked}
                    onClose={() => setHistoryOpen(false)}
                    refreshKey={status}
                    saving={status === "saving"}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onRestore={restoreVersion}
                />
            ) : null}
            <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.doc,.rtf,.sfdt,.txt"
                hidden
                onChange={onLocalPick}
            />
            {error ? (
                <p className="flex shrink-0 items-start gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{error}</span>
                </p>
            ) : null}

            <div className="relative flex min-h-0 flex-1">
                {outlineOpen ? (
                    <OutlinePane
                        items={outline}
                        currentKey={currentKey}
                        onNavigate={goToHeading}
                        onClose={() => setOutlineOpen(false)}
                    />
                ) : null}
                <div className="relative min-h-0 min-w-0 flex-1">
                    <Suspense
                        fallback={
                            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading editor…
                            </div>
                        }
                    >
                        <DocumentEditorHost
                            editorRef={editorRef}
                            accessToken={token}
                            onCreated={onEditorCreated}
                            onContentChange={onEditorContentChange}
                            onDocumentChange={onEditorDocumentChange}
                            onSelectionChange={onEditorSelection}
                            onTrackChange={onTrackChange}
                            onToolbarClick={onToolbarClick}
                            fileMenuItems={FILE_MENU_ITEMS}
                            onFileMenuItemClick={onFileMenuItemClick}
                        />
                    </Suspense>
                </div>
                {commentsOpen && tracked ? (
                    // Docked beside the page on lg+ (mock `.side.r`); an overlay below that.
                    <CommentsPanel
                        key={tracked.id}
                        file={tracked}
                        adapter={commentsAdapter}
                        docked
                        onClose={() => setCommentsOpen(false)}
                        onOpenCountChange={commentCount.set}
                    />
                ) : null}
                {reviewOpen ? (
                    <ReviewPane
                        revisions={revisions}
                        canAct={canActOnRevisions}
                        suggesting={effectiveMode === "suggesting"}
                        onAccept={(rev: DocRevision) => revisionAction(() => rev.accept())}
                        onReject={(rev: DocRevision) => revisionAction(() => rev.reject())}
                        onAcceptAll={() => revisionAction((ed) => ed.revisions.acceptAll())}
                        onRejectAll={() => revisionAction((ed) => ed.revisions.rejectAll())}
                        onSelect={(rev: DocRevision) => { try { rev.select(); } catch { /* gone */ } }}
                        onClose={() => setReviewOpen(false)}
                    />
                ) : null}
            </div>
            <NativeStatusBarSlot container={editorReady ? editorRef.current : null}>
                <DocStatusItems
                    stats={stats}
                    selectionWords={selectionWords}
                    outlineOpen={outlineOpen}
                    onToggleOutline={() => setOutlineOpen((v) => !v)}
                    suggestionCount={revisions.length}
                    reviewOpen={reviewOpen}
                    onToggleReview={reviewOpen ? () => setReviewOpen(false) : openReview}
                />
            </NativeStatusBarSlot>
            <NativeStatusBarSlot container={editorReady ? editorRef.current : null} placement="end">
                <DocSaveStatusItem
                    status={status}
                    inDrive={tracked !== null}
                    loading={loading}
                    onRetry={() => void saveNowRef.current("manual")}
                />
            </NativeStatusBarSlot>
            {driveDialogOpen ? (
                <DriveOpenDialog onClose={() => setDriveDialogOpen(false)} onPick={pickDriveFile} />
            ) : null}
            {openChoiceOpen ? (
                <OpenChoiceDialog
                    onClose={() => setOpenChoiceOpen(false)}
                    onPickComputer={() => { setOpenChoiceOpen(false); fileInputRef.current?.click(); }}
                    onPickDrive={() => { setOpenChoiceOpen(false); void openFromDrive(); }}
                />
            ) : null}
            {saveAsOpen ? (
                <SaveAsDialog
                    defaultName={stripExtension(fileName)}
                    defaultType={inferDocType(fileName)}
                    onClose={() => setSaveAsOpen(false)}
                    onConfirm={handleSaveAsConfirm}
                />
            ) : null}
        </div>
    );
}

function inferDocType(name: string): DocFormat {
    return versionFormat(name) ?? "docx";
}

/**
 * The format Save must write to version `name` in place, or null when the
 * editor can't produce that format (.doc / .rtf) — a version has to keep
 * the original file's format.
 */
function versionFormat(name: string): DocFormat | null {
    const ext = name.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
    return ext === "docx" || ext === "sfdt" || ext === "txt" ? ext : null;
}

function toTracked(item: DriveItem): TrackedFile {
    return { id: item.id, name: item.name, parentId: item.parentId, role: item.role ?? null };
}

function unsupportedMessage(name: string): string {
    return `${name} can't be opened in Athavita Docs. Supported file types are .docx, .doc, .rtf, .sfdt and .txt.`;
}

function errorMessage(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

function assertNotEmpty(blob: Blob) {
    if (blob.size === 0) throw new Error("The editor produced an empty file — nothing was saved.");
}

function downloadFile(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

// Keeps the file's extension when the user types a bare name ("Q3 plan" →
// "Q3 plan.docx"). A different document extension is swapped for the
// current one — a rename must never change what format the bytes are in.
function keepExtension(typed: string, current: string): string {
    const trimmed = typed.trim();
    const ext = current.match(/\.([^./\\]+)$/)?.[1];
    if (!ext) return trimmed;
    if (trimmed.toLowerCase().endsWith(`.${ext.toLowerCase()}`) && trimmed.length > ext.length + 1) return trimmed;
    const base = trimmed.replace(NATIVE_SUPPORTED_EXT_RE, "").trim();
    if (!base) throw new Error("Enter a file name.");
    return `${base}.${ext}`;
}

function OpenChoiceDialog({
    onClose, onPickComputer, onPickDrive
}: {
    onClose: () => void;
    onPickComputer: () => void;
    onPickDrive: () => void;
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                className="w-[400px] max-w-[92vw] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-sm font-semibold">Open document</h2>
                    <button type="button" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">✕</button>
                </div>
                <div className="flex flex-col gap-2 p-4">
                    <button
                        type="button"
                        onClick={onPickComputer}
                        className="flex items-center gap-3 rounded-md border p-3 text-left hover:bg-accent"
                    >
                        <span className="e-icons e-folder-open text-2xl text-primary" aria-hidden />
                        <span className="flex-1">
                            <div className="text-sm font-medium">From this computer</div>
                            <div className="text-xs text-muted-foreground">Pick a .docx / .doc / .rtf / .sfdt / .txt file</div>
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={onPickDrive}
                        className="flex items-center gap-3 rounded-md border p-3 text-left hover:bg-accent"
                    >
                        <span className="e-icons e-upload text-2xl text-primary" aria-hidden />
                        <span className="flex-1">
                            <div className="text-sm font-medium">From Drive</div>
                            <div className="text-xs text-muted-foreground">Browse your Athavita Drive library</div>
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function SaveAsDialog({
    defaultName, defaultType, onClose, onConfirm
}: {
    defaultName: string;
    defaultType: DocFormat;
    onClose: () => void;
    onConfirm: (target: "drive" | "computer", name: string, type: DocFormat) => void;
}) {
    const [target, setTarget] = useState<"drive" | "computer">("drive");
    const [name, setName] = useState(defaultName);
    const [type, setType] = useState<DocFormat>(defaultType);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                className="w-[460px] max-w-[92vw] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-sm font-semibold">Save As</h2>
                    <button type="button" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">✕</button>
                </div>
                <form
                    className="flex flex-col gap-3 p-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (name.trim()) onConfirm(target, name.trim(), type);
                    }}
                    id="docs-save-as-form"
                >
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Save to</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setTarget("drive")}
                                className={`flex-1 rounded-md border px-3 py-2 text-sm ${target === "drive" ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}
                            >
                                <span className="e-icons e-upload mr-1" aria-hidden /> Drive
                            </button>
                            <button
                                type="button"
                                onClick={() => setTarget("computer")}
                                className={`flex-1 rounded-md border px-3 py-2 text-sm ${target === "computer" ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}
                            >
                                <span className="e-icons e-folder-open mr-1" aria-hidden /> Computer
                            </button>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="save-as-name" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Filename</label>
                        <input
                            id="save-as-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        />
                    </div>
                    <div>
                        <label htmlFor="save-as-type" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">File type</label>
                        <select
                            id="save-as-type"
                            value={type}
                            onChange={(e) => setType(e.target.value as DocFormat)}
                            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                            <option value="docx">Word Document (.docx)</option>
                            <option value="sfdt">Syncfusion Document (.sfdt)</option>
                            <option value="txt">Plain text (.txt)</option>
                        </select>
                    </div>
                </form>
                <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3">
                    <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm hover:bg-accent">Cancel</button>
                    <button
                        type="submit"
                        form="docs-save-as-form"
                        disabled={!name.trim()}
                        className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

async function openBlobInEditor(
    editor: DocEditor,
    blob: Blob,
    name: string,
    /** For the Docs service's Import (moving behind sign-in). */
    token?: string
): Promise<void> {
    if (SFDT_EXTENSION_RE.test(name)) {
        const text = await blob.text();
        editor.open(text);
        return;
    }
    if (TEXT_EXTENSION_RE.test(name)) {
        const text = await blob.text();
        const minimalSfdt = buildTextSfdt(text);
        editor.open(minimalSfdt);
        return;
    }
    if (SERVER_CONVERTED_EXT_RE.test(name)) {
        // Two paths, in order of preference:
        //   1. `VITE_DOCS_SERVICE_URL` — self-hosted Syncfusion
        //      WordEditor import endpoint returns proper SFDT. Best
        //      fidelity (styles, tables, images, footnotes).
        //   2. Fallback to mammoth.js client-side DOCX→HTML → inject
        //      the HTML into a blank editor. Lower fidelity (text +
        //      basic formatting only), but zero server dependency
        //      and works offline — the default after Syncfusion's
        //      public demo service went 404.
        const serviceBase = import.meta.env.VITE_DOCS_SERVICE_URL as string | undefined;
        if (serviceBase) {
            const file = blob instanceof File ? blob : new File([blob], name);
            const form = new FormData();
            form.append("files", file);
            const res = await fetch(`${serviceBase.replace(/\/$/, "")}/Import`, {
                method: "POST",
                body: form,
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                credentials: "omit"
            });
            if (res.ok) {
                const sfdt = await res.text();
                if (sfdt && sfdt.length > 10) {
                    editor.open(sfdt);
                    return;
                }
            }
            // fall through to mammoth if the service failed
        }

        const arrayBuffer = await blob.arrayBuffer();
        const mammoth = await import("mammoth");
        const { value: html } = await mammoth.convertToHtml({ arrayBuffer });
        if (!html) throw new Error(`Empty document — cannot open ${name}`);
        // Open a blank doc first, then insert the HTML. DocumentEditor's
        // `editor.insertHtml` preserves paragraphs, bold/italic, lists,
        // and basic tables. Images are dropped (mammoth returns them as
        // inline base64 which the editor rejects without extra wiring).
        editor.openBlank();
        const e = editor.editor as unknown as { insertHtml?: (html: string) => void };
        if (typeof e.insertHtml === "function") {
            e.insertHtml(html);
        } else {
            // Defensive: some builds don't expose insertHtml; fall back
            // to plain-text so the user sees SOMETHING.
            const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
            editor.editor.insertText(plain);
        }
        return;
    }
    throw new Error(unsupportedMessage(name));
}

function buildTextSfdt(text: string): string {
    // Minimal SFDT wrapper — one section, one paragraph per line of input.
    // Good enough for .txt preview; formatting is plain.
    const paragraphs = text.split(/\r?\n/).map((line) => ({
        paragraphFormat: { styleName: "Normal" },
        inlines: line.length === 0 ? [] : [{ characterFormat: {}, text: line }]
    }));
    return JSON.stringify({
        sections: [{ blocks: paragraphs, sectionFormat: {} }]
    });
}

function stripExtension(name: string): string {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return "Untitled";
    return trimmed.replace(/\.[^./\\]+$/, "");
}
